import {
  IconArrowRight,
  IconAward,
  IconBuildingBank,
  IconChevronUp,
  IconListCheck,
  IconRocket,
} from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { useEffect, useMemo, useRef } from "react";

import { analytics } from "../../analytics/analyticsEvents";
import { useLocalize } from "../../content/useLocalize";
import {
  achievements as allAchievements,
  clients,
  contracts,
  getClientById,
  getContractById,
} from "../../content";
import { getGrade, getRewardMultiplier, getTrustDelta } from "../../engine/scoring";
import {
  DEFAULT_LEVEL_CAP,
  REPLAY_REWARD_MULTIPLIER,
  getNextUnlockClient,
  getRecommendedContract,
  levelThreshold,
} from "../../engine/progression";
import { contractTypeText } from "../../i18n/enumText";
import { taskBreakdownRows } from "./taskBreakdown";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { GradeSeal } from "../common/GradeSeal";
import { Panel } from "../common/Panel";
import { ProgressBar } from "../common/ProgressBar";
import { StatTile } from "../common/StatTile";
import { cx } from "../common/cx";
import { formatMoney, formatNumber } from "../common/format";
import styles from "./ResultScreen.module.css";

export function ResultScreen(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const lastResult = useGameStore((state) => state.lastResult);
  const player = useGameStore((state) => state.player);
  const clientTrust = useGameStore((state) => state.clientTrust);
  const unlockedClients = useGameStore((state) => state.unlockedClients);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const setActiveContract = useGameStore((state) => state.setActiveContract);

  useEffect(() => {
    if (lastResult && !lastResult.readOnly) {
      analytics.resultViewed({ grade: lastResult.summary.grade });
    }
  }, [lastResult]);

  if (!lastResult) {
    return (
      <EmptyState
        title={<Trans>Нет результата</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("dashboard")}>
            <Trans>В штаб</Trans>
          </Button>
        }
      />
    );
  }

  const { summary, newAchievements, taskScores, readOnly } = lastResult;
  const {
    score,
    grade,
    reward,
    previousLevel,
    newLevel,
    leveledUp,
    skillPointsGained,
    urgentBonusPct,
    trustBonusPct,
  } = summary;
  const urgentPctDisplay = Math.round(urgentBonusPct * 100);
  const trustPctDisplay = Math.round(trustBonusPct * 100);
  const replay = lastResult?.summary.replay ?? false;
  const replayPctDisplay = REPLAY_REWARD_MULTIPLIER;
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const contract = getContractById(lastResult.contractId);
  const client = contract ? getClientById(contract.clientId) : undefined;
  const trustDelta = getTrustDelta(grade);
  const newTrust = contract ? clientTrust[contract.clientId] ?? 0 : 0;
  const failed = grade === "Failed";
  const rewardMultiplier = getRewardMultiplier(grade);

  const atMaxLevel = newLevel >= DEFAULT_LEVEL_CAP;
  const levelXpInLevel = Math.max(0, player.xp - levelThreshold(newLevel));
  const levelXpNeeded = Math.max(1, levelThreshold(newLevel + 1) - levelThreshold(newLevel));
  const levelXpCurrentLabel = formatNumber(levelXpInLevel);
  const levelXpTargetLabel = formatNumber(levelXpNeeded);
  const nextLevelLabel = newLevel + 1;

  const newAchievementList = useMemo(
    () => allAchievements.filter((achievement) => newAchievements.includes(achievement.id)),
    [newAchievements],
  );

  const nextUnlock = useMemo(
    () => getNextUnlockClient(clients, unlockedClients),
    [unlockedClients],
  );
  const nextUnlockRep = nextUnlock?.requiredReputation ?? 0;

  const nextContract = useMemo(() => getRecommendedContract(contracts, player), [player]);
  const breakdownRows = useMemo(
    () => taskBreakdownRows(taskScores ?? [], lastResult?.taskTypes, contract?.tasks),
    [taskScores, lastResult?.taskTypes, contract],
  );

  const goNext = (): void => {
    if (nextContract) {
      setActiveContract(nextContract.id);
      navigateTo("contract_details");
    } else {
      navigateTo("dashboard");
    }
  };

  return (
    <main className={styles.screen}>
      <header className={styles.head}>
        <h1 className={styles.headTitle} tabIndex={-1} ref={titleRef}>
          {readOnly ? <Trans>Просмотр результата</Trans> : <Trans>Контракт завершён</Trans>}
        </h1>
        {client && <span className={styles.headClient}>{t(client.name)}</span>}
        {contract && (
          <span className={styles.headTask}>{i18n._(contractTypeText(contract.primaryTask))}</span>
        )}
      </header>

      <div className={styles.hero} data-tutorial="grade-seal">
        <GradeSeal grade={grade} size="large" showMultiplier />
        <div className={cx(styles.score, "mono")}>
          <Trans>Счёт: {score}</Trans>
        </div>
        <p className={styles.scoreHint}>
          <Trans>
            Средний результат по задачам - от него зависят оценка (S/A/B/C) и множитель награды.
          </Trans>
        </p>
        {failed && !readOnly && (
          <p className={styles.failedNote}>
            <Trans>
              Контракт провален - награда сильно урезана. Разбери ошибки и пройди контракт снова.
            </Trans>
          </p>
        )}
      </div>

      {breakdownRows.length > 0 && (
        <Panel title={<Trans>Разбор по задачам</Trans>} icon={<IconListCheck size={18} />}>
          <ul className={styles.taskBreakdown}>
            {breakdownRows.map((row) => {
              const position = row.index + 1;
              const label = row.type
                ? i18n._(contractTypeText(row.type))
                : i18n._(msg`Задача ${position}`);
              return (
                <li key={row.index} className={styles.taskRow}>
                  <span className={styles.taskName}>{label}</span>
                  <GradeSeal grade={getGrade(row.score)} score={row.score} />
                </li>
              );
            })}
          </ul>
        </Panel>
      )}

      <Panel title={<Trans>Награды</Trans>}>
        {urgentBonusPct > 0 && (
          <span className={styles.urgentBadge}>
            <Trans>Срочный +{urgentPctDisplay}%</Trans>
          </span>
        )}
        {trustBonusPct > 0 && (
          <span className={styles.trustBadge}>
            <Trans>Доверенный клиент +{trustPctDisplay}% к оплате</Trans>
          </span>
        )}
        {replay && (
          <span className={styles.replayBadge}>
            <Trans>Повтор контракта: награда ×{replayPctDisplay}</Trans>
          </span>
        )}
        <div className={styles.rewards} data-tutorial="result-rewards">
          <StatTile
            label={<Trans>Деньги</Trans>}
            value={`+${formatMoney(reward.money)}`}
            tone="money"
          />
          <StatTile label={<Trans>Опыт</Trans>} value={`+${formatNumber(reward.xp)}`} />
          {!readOnly && (
            <StatTile
              label={<Trans>Репутация</Trans>}
              value={`+${formatNumber(reward.reputation)}`}
            />
          )}
          {!readOnly && client && (
            <StatTile
              label={<Trans>Доверие</Trans>}
              value={`${trustDelta >= 0 ? "+" : ""}${formatNumber(trustDelta)} → ${newTrust} / 100`}
              icon={<IconBuildingBank size={14} />}
            />
          )}
        </div>
        <p className={styles.multiplierNote}>
          <Trans>
            Множитель за грейд {grade}: ×{rewardMultiplier}
          </Trans>
        </p>
      </Panel>

      {leveledUp && (
        <Panel title={<Trans>Новый уровень</Trans>} icon={<IconChevronUp size={18} />}>
          <div className={styles.levelUp}>
            <Trans>
              Уровень {previousLevel} → {newLevel}
            </Trans>
            <span className={styles.spGain}>
              <Trans>+{skillPointsGained} очко навыка</Trans>
            </span>
          </div>
          {!atMaxLevel && (
            <div className={styles.levelXp}>
              <ProgressBar value={levelXpInLevel} max={levelXpNeeded} context="accent" size="mini" label={i18n._(msg`Прогресс опыта`)} />
              <div className={styles.levelXpMeta}>
                <span>
                  <Trans>До ур. {nextLevelLabel}</Trans>
                </span>
                <span className="mono">
                  <Trans>
                    {levelXpCurrentLabel} / {levelXpTargetLabel} XP
                  </Trans>
                </span>
              </div>
            </div>
          )}
        </Panel>
      )}

      {newAchievementList.length > 0 && (
        <Panel title={<Trans>Новые достижения</Trans>} icon={<IconAward size={18} />}>
          <ul className={styles.achievements}>
            {newAchievementList.map((achievement) => (
              <li key={achievement.id} className={styles.achievement}>
                <IconAward size={17} aria-hidden="true" />
                {t(achievement.name)}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {!readOnly && nextUnlock && (
        <Panel title={<Trans>Скоро откроется</Trans>} icon={<IconRocket size={18} />}>
          <div className={styles.unlock}>
            <span>{t(nextUnlock.name)}</span>
            <span className={styles.unlockCond}>
              <Trans>Откроется при репутации {nextUnlockRep}</Trans>
            </span>
          </div>
          <ProgressBar value={player.reputation} max={nextUnlockRep || 1} context="accent" label={i18n._(msg`Репутация`)} />
        </Panel>
      )}

      <div className={styles.actions}>
        {readOnly ? (
          <Button
            variant="primary"
            icon={<IconArrowRight size={17} />}
            onClick={() => navigateTo("dashboard")}
          >
            <Trans>В штаб</Trans>
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => navigateTo("dashboard")}>
              <Trans>В штаб</Trans>
            </Button>
            <Button variant="primary" icon={<IconArrowRight size={17} />} onClick={goNext}>
              {nextContract ? <Trans>Следующий контракт</Trans> : <Trans>К контрактам</Trans>}
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
